import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

const isRealyncUrl = (url: string) => /(^|\/\/|\.)realync\.com/i.test(url);

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  useEffect(() => {
    if (selectedIndex >= validTours.length) {
      setSelectedIndex(0);
    }
  }, [selectedIndex, validTours.length]);

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  const measureRef = useRef<HTMLDivElement>(null);
  const [useDropdown, setUseDropdown] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const el = measureRef.current;
    if (!hasMultiple || !el) {
      setUseDropdown(false);
      return;
    }
    const check = () => {
      const labels = el.querySelectorAll<HTMLElement>('[data-tour-label]');
      setUseDropdown(
        Array.from(labels).some((label) => label.scrollWidth > label.clientWidth + 1),
      );
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMultiple, validTours]);

  const tabClassName =
    'font-body w-full min-w-0 bg-primary px-3 py-3.5 text-center text-sm text-primary-foreground transition-colors hover:bg-primary hover:text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-inset md:text-base lg:flex-1 aria-selected:bg-primary aria-selected:text-primary-foreground';
  const tourLabel = (tour: VirtualTour, i: number) =>
    tour.virtual_tour_title || `Tour ${i + 1}`;

  return (
    <section className="bg-white py-8 md:py-12">
      <EditableCollectionBinding
        bindingPath="virtualtourembed.tours"
        label="Virtual Tours"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden">
            {hasMultiple && (
              <div
                ref={measureRef}
                aria-hidden="true"
                className="pointer-events-none invisible absolute inset-x-0 top-0 flex w-full flex-col gap-px lg:flex-row"
              >
                {validTours.map((tour, i) => (
                  <div key={i} className={`${tabClassName} font-semibold`}>
                    <span data-tour-label className="block truncate">
                      {tourLabel(tour, i)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {hasMultiple && useDropdown && (
              <div className="relative mb-6 lg:mb-8">
                <label htmlFor="virtual-tour-select" className="sr-only">
                  Select a virtual tour
                </label>
                <select
                  id="virtual-tour-select"
                  value={selectedIndex}
                  onChange={(e) => setSelectedIndex(Number(e.target.value))}
                  aria-controls="virtual-tour-panel"
                  className="font-body block w-full cursor-pointer appearance-none truncate rounded-none border-0 bg-primary py-3.5 pl-4 pr-12 text-sm font-semibold text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-inset md:text-base"
                >
                  {validTours.map((tour, i) => (
                    <option key={i} value={i} className="bg-white text-[var(--vibe-color-text)]">
                      {tourLabel(tour, i)}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  aria-hidden="true"
                  className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primary-foreground"
                />
              </div>
            )}

            {hasMultiple && !useDropdown && (
              <div
                role="tablist"
                aria-label="Virtual tours"
                className="mb-6 flex w-full flex-col gap-px lg:mb-8 lg:flex-row"
              >
                {validTours.map((tour, i) => {
                  const isActive = i === selectedIndex;
                  return (
                    <button
                      key={i}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      id={`virtual-tour-tab-${i}`}
                      aria-controls="virtual-tour-panel"
                      onClick={() => setSelectedIndex(i)}
                      className={`${tabClassName} ${
                        isActive ? 'font-semibold' : 'font-medium'
                      }`}
                    >
                      <span className="block truncate">
                        {tourLabel(tour, i)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <div
              id="virtual-tour-panel"
              role={hasMultiple && !useDropdown ? 'tabpanel' : 'region'}
              aria-labelledby={
                hasMultiple && !useDropdown
                  ? `virtual-tour-tab-${selectedIndex}`
                  : undefined
              }
              aria-label={
                hasMultiple && !useDropdown
                  ? undefined
                  : activeTour?.virtual_tour_title || 'Virtual Tour'
              }
            >
              {activeTour?.virtual_tour_url ? (
                <div
                  className={
                    isRealyncUrl(activeTour.virtual_tour_url)
                      ? 'relative w-full h-[85svh] min-h-[640px] max-h-[960px]'
                      : 'relative w-full'
                  }
                  style={
                    isRealyncUrl(activeTour.virtual_tour_url)
                      ? undefined
                      : { paddingBottom: '56.25%' }
                  }
                >
                  <iframe
                    key={activeTour.virtual_tour_url}
                    src={activeTour.virtual_tour_url}
                    className="absolute inset-0 w-full h-full border-0"
                    allowFullScreen
                    title={activeTour.virtual_tour_title || 'Virtual Tour'}
                  />
                </div>
              ) : (
                <div className="aspect-video w-full bg-[var(--vibe-color-surface)] flex items-center justify-center">
                  <p className="text-[var(--vibe-color-text-muted)] font-body">
                    No virtual tours are available for this property.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </EditableCollectionBinding>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default VirtualTourEmbed;
